/**
 * Atrybucja polowa CLS — namierza KONKRETNY element powodujący największy
 * layout-shift u realnych użytkowników. Headless (Playwright, szybkie łącze, brak
 * aktywnej kampanii Segmentify) nie odtwarza field CLS 0,56 — jedyne wiarygodne
 * źródło winowajcy to pomiar w polu.
 *
 * CLS liczymy algorytmem "sesyjnego okna" jak biblioteka web-vitals (grupowanie
 * shiftów: przerwa <1s i długość okna <5s; CLS = największa suma pojedynczego okna),
 * więc wartość jest porównywalna z tym, co raportuje CrUX/PageSpeed. Dodatkowo
 * zapamiętujemy największy POJEDYNCZY shift i selektor CSS jego źródłowego węzła.
 *
 * Raport wysyłamy, gdy karta znika (visibilitychange→hidden / pagehide) do:
 *   - dataLayer (event `cls_debug`) → GA4, jeśli event podpięty w GTM,
 *   - Microsoft Clarity (custom tag `cls_target`/`cls_value`) → filtrowalne + nagrania,
 *   - console.debug (podgląd w DevTools na prod).
 * Bez dodatkowej zależności npm. Tylko produkcja; no-op bez PerformanceObserver /
 * bez wsparcia layout-shift (np. Safari).
 */
export default defineNuxtPlugin(() => {
  if (import.meta.env.MODE !== "production") return;
  if (typeof PerformanceObserver === "undefined") return;
  if (!PerformanceObserver.supportedEntryTypes?.includes("layout-shift")) return;

  interface LayoutShiftEntry extends PerformanceEntry {
    value: number;
    hadRecentInput: boolean;
    sources?: { node?: Node | null }[];
  }

  // Zwięzły, czytelny selektor źródłowego węzła shiftu (tag#id.klasa, do 3 poziomów w górę).
  const selectorFor = (node: Node | null | undefined): string => {
    let el: Element | null =
      node && node.nodeType === 1 ? (node as Element) : null;
    const parts: string[] = [];
    for (let depth = 0; el && depth < 3; depth++) {
      const tag = el.tagName.toLowerCase();
      if (el.id) {
        parts.unshift(`${tag}#${el.id}`);
        break;
      }
      const classes = (el.getAttribute("class") || "")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((c) => `.${c}`)
        .join("");
      parts.unshift(tag + classes);
      el = el.parentElement;
    }
    return parts.join(" > ") || "unknown";
  };

  let cls = 0;
  let sessionValue = 0;
  let sessionEntries: LayoutShiftEntry[] = [];
  const largest = { value: 0, target: "none" };

  const obs = new PerformanceObserver((list) => {
    for (const entry of list.getEntries() as LayoutShiftEntry[]) {
      if (entry.hadRecentInput) continue;
      const first = sessionEntries[0];
      const last = sessionEntries[sessionEntries.length - 1];
      if (
        sessionValue &&
        first &&
        last &&
        entry.startTime - last.startTime < 1000 &&
        entry.startTime - first.startTime < 5000
      ) {
        sessionValue += entry.value;
        sessionEntries.push(entry);
      } else {
        sessionValue = entry.value;
        sessionEntries = [entry];
      }
      if (sessionValue > cls) cls = sessionValue;
      if (entry.value > largest.value) {
        const node = entry.sources?.find(
          (s) => s.node && s.node.nodeType === 1,
        )?.node;
        largest.value = entry.value;
        largest.target = selectorFor(node);
      }
    }
  });
  obs.observe({ type: "layout-shift", buffered: true });

  const round = (n: number) => Math.round(n * 1000) / 1000;
  let lastReported = -1;
  const report = () => {
    obs.takeRecords?.();
    if (round(cls) === lastReported) return; // dedup identycznych zgłoszeń
    lastReported = round(cls);
    const payload = {
      cls_value: round(cls),
      cls_largest_value: round(largest.value),
      cls_largest_target: largest.target,
    };
    const w = window as unknown as {
      dataLayer?: Record<string, unknown>[];
      clarity?: (cmd: string, ...args: unknown[]) => void;
    };
    (w.dataLayer ||= []).push({ event: "cls_debug", ...payload });
    w.clarity?.("set", "cls_target", payload.cls_largest_target);
    w.clarity?.("set", "cls_value", String(payload.cls_value));
    console.debug("[cls-attribution]", payload);
  };

  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") report();
  });
  window.addEventListener("pagehide", report);
});
